import React from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INPUT_BOX_CLASS, INPUT_BOX_STYLE, ELIGIBILITY_PEER_DIM_STYLE } from "./checkerFieldStyles";
import ChecklistItem from "./ChecklistItem";
import { ChoiceGroup, YesNoChoice } from "./ChoiceCard";
import FieldError from "./FieldError";
import { fieldAnchorProps, invalidInputProps } from "./checkerValidation";

const documentOptions = [
  "ACRA Business Profile",
  "Financial statements (last 3 years)",
  "Project quotations/estimates",
  "Company organizational chart",
  "Project proposal/business plan",
  "Vendor proposals/contracts",
  "Tax clearance certificate",
  "CPF clearance certificate"
];

/** Exclusive "nothing yet" answer for the documents checklist (satisfies the at-least-one rule honestly). */
export const NO_DOCUMENTS_YET = "None of these yet";

/**
 * Compliance declarations as direct questions. `yesValue` is the boolean stored
 * for a "Yes" answer (the stored keys are phrased positively, e.g.
 * `cpf_compliance_ok`, so "Yes, there is a CPF issue" stores `false`).
 * Notes explain what the answer means for the assessment.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const DECLARATION_QUESTIONS = [
  {
    key: "no_outstanding_issues",
    question: "Does your company have any outstanding issues with a government agency?",
    yesValue: false,
    yesDescription: "There are pending matters with one or more agencies.",
    noDescription: "No pending matters with any government agency.",
    yesNote: "Agencies usually ask for open matters to be resolved before approving a grant. The assessment will flag this on every scheme and suggest what to clear first.",
  },
  {
    key: "meets_local_shareholding",
    question: "Is at least 30% of the company owned by Singapore citizens or permanent residents?",
    yesValue: true,
    yesDescription: "Local ownership is 30% or more.",
    noDescription: "Local ownership is below 30%.",
    noNote: "Most Enterprise Singapore schemes require 30% local shareholding. The assessment will separate the schemes this rules out from the ones still open to you.",
  },
  {
    key: "authorized_to_apply",
    question: "Are you authorised to submit grant applications for this company?",
    yesValue: true,
    yesDescription: "I can sign or submit on the company's behalf.",
    noDescription: "Someone else will need to sign off.",
    noNote: "You can still run the assessment. An authorised officer (usually a director) will need to approve any application before it is submitted.",
  },
  {
    key: "cpf_compliance_ok",
    question: "Are there any outstanding CPF contributions or CPF-related issues?",
    yesValue: false,
    yesDescription: "Contributions are overdue or a CPF matter is open.",
    noDescription: "CPF contributions are up to date.",
    yesNote: "Agencies check CPF records at application. The assessment will mark this as a pre-application fix rather than an eligibility block.",
  },
  {
    key: "tax_compliance_ok",
    question: "Are there any outstanding tax filings, tax payments or IRAS disputes?",
    yesValue: false,
    yesDescription: "A filing, payment or dispute is outstanding.",
    noDescription: "Tax filings and payments are up to date.",
    yesNote: "Outstanding tax matters are typically resolved before an application is approved. The assessment will flag this as a pre-application fix.",
  },
  {
    key: "regulatory_compliance_ok",
    question: "Is the company facing any ongoing legal, regulatory or compliance action?",
    yesValue: false,
    yesDescription: "An investigation, regulatory action or major compliance issue is ongoing.",
    noDescription: "No ongoing legal, regulatory or compliance action.",
    yesNote: "Ongoing actions are weighed on a case-by-case basis by agencies. The assessment will note the schemes where this is most likely to matter.",
  },
  {
    key: "prior_grant_breach",
    question: "Has the company ever defaulted on, breached the terms of, or had a clawback on a government grant?",
    yesValue: true,
    yesDescription: "There has been a default, breach or clawback.",
    noDescription: "No history of default, breach or clawback.",
    yesNote: "A prior breach does not disqualify you outright, but agencies may apply extra scrutiny. The assessment will reflect this in the readiness guidance.",
  },
  {
    key: "project_not_commenced",
    question: "Has the project already started, or has any payment, deposit or contract for it been committed?",
    yesValue: false,
    yesDescription: "Work has started or money has been committed.",
    noDescription: "Nothing has started or been committed yet.",
    yesNote: "Most Singapore grants only fund work that starts after approval. The assessment will flag which schemes this affects and whether a later phase could still qualify.",
  },
  {
    key: "no_debarment_or_insolvency",
    question: "Has the company or any of its directors been debarred, made insolvent, or been subject to winding-up?",
    yesValue: false,
    yesDescription: "There has been a debarment, insolvency or winding-up event.",
    noDescription: "No debarment, insolvency or winding-up events.",
    yesNote: "These events usually rule a company out of government grants until resolved. The assessment will say so plainly rather than score schemes that are not open to you.",
  },
];

const financialRecencyOptions = [
  "Within 6 months",
  "6–12 months",
  "12–24 months",
  "More than 24 months",
  "Not available"
];

export default function ComplianceStep({ formData, updateFormData, errors = {} }) {
  const availableDocs = Array.isArray(formData.available_documents) ? formData.available_documents : [];
  const noneSelected = availableDocs.includes(NO_DOCUMENTS_YET);

  const handleDocumentChange = (document, checked) => {
    if (document === NO_DOCUMENTS_YET) {
      // Exclusive: clears every real document when chosen.
      updateFormData({ available_documents: checked ? [NO_DOCUMENTS_YET] : [] });
      return;
    }
    const withoutNone = availableDocs.filter((doc) => doc !== NO_DOCUMENTS_YET);
    if (checked) {
      updateFormData({ available_documents: [...withoutNone, document] });
    } else {
      updateFormData({ available_documents: withoutNone.filter(doc => doc !== document) });
    }
  };

  const setDeclaration = (key, value) => {
    updateFormData({
      declarations: {
        ...formData.declarations,
        [key]: value,
      },
    });
  };

  return (
    <div id="step-compliance" className="space-y-10">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ color: '#4B5563' }} tabIndex={-1} data-step-heading>Compliance & Documentation</h2>
        <p style={{ color: '#4B5563' }}>Verify your readiness with required documents and declarations</p>
      </div>

      {/* Documents */}
      <div className="w-full rounded-3xl bg-white/5 backdrop-blur-xl shadow-2xl border border-white/10 p-6 md:p-8">
        <ChoiceGroup
          name="available_documents"
          role="group"
          legend="Available Documents"
          required
          hint="Select all documents you currently have or can obtain within 2 weeks. If you have none of these yet, say so; it changes the readiness guidance, not your eligibility."
          error={errors.available_documents}
          className="space-y-6"
          optionsClassName="space-y-0"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0">
            {documentOptions.map(doc => (
              <div key={doc} style={noneSelected ? ELIGIBILITY_PEER_DIM_STYLE : undefined}>
                <ChecklistItem
                  id={`doc-${doc}`}
                  label={doc}
                  checked={availableDocs.includes(doc)}
                  onCheckedChange={(checked) => handleDocumentChange(doc, checked)}
                  compact={true}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-white/10 pt-3">
            <ChecklistItem
              id="doc-none-yet"
              label={NO_DOCUMENTS_YET}
              description="We have not prepared any of the documents above yet."
              checked={noneSelected}
              onCheckedChange={(checked) => handleDocumentChange(NO_DOCUMENTS_YET, checked)}
              compact={false}
            />
          </div>
        </ChoiceGroup>
      </div>

      {/* Compliance Declarations: direct yes/no questions, each a radiogroup */}
      <div className="w-full rounded-3xl bg-white/5 backdrop-blur-xl shadow-2xl border border-white/10 p-6 md:p-8">
        <div className="space-y-6">
        <div className="flex items-center gap-2">
          <p className="font-medium text-lg" style={{ color: '#4B5563' }}>
            Compliance Declarations <span style={{ color: '#4B5563' }}>*</span>
          </p>
        </div>
        <FieldError name="declarations" message={errors.declarations} />

        <div className="space-y-6" {...fieldAnchorProps('declarations')}>
          {DECLARATION_QUESTIONS.map((q) => {
            const current = formData.declarations?.[q.key];
            const answered = current === true || current === false;
            const answeredYes = answered && current === q.yesValue;
            const note = answered ? (answeredYes ? q.yesNote : q.noNote) : null;
            return (
              <ChoiceGroup
                key={q.key}
                name={`declaration_${q.key}`}
                legend={q.question}
                variant="question"
                required
                note={note}
                error={errors[`declaration_${q.key}`]}
              >
                <YesNoChoice
                  name={`declaration_${q.key}`}
                  value={current}
                  yesValue={q.yesValue}
                  onChange={(next) => setDeclaration(q.key, next)}
                  yesLabel="Yes"
                  yesDescription={q.yesDescription}
                  noLabel="No"
                  noDescription={q.noDescription}
                />
              </ChoiceGroup>
            );
          })}

          {/* Document recency */}
          <div className="checker-document-recency space-y-2" {...fieldAnchorProps('financials_recency')}>
            <Label id="financials_recency-label" htmlFor="financials_recency" className="font-medium" style={{ color: '#4B5563' }}>
              Document Recency <span style={{ color: '#4B5563' }}>*</span>
            </Label>
            <p id="financials_recency-hint" className="text-sm" style={{ color: '#4B5563' }}>
              How recent are your latest financial statements or management accounts?
            </p>
            <Select
              value={formData.financials_recency || ''}
              onValueChange={(value) => updateFormData({ financials_recency: value })}
            >
              <SelectTrigger
                id="financials_recency"
                aria-labelledby="financials_recency-label"
                className={INPUT_BOX_CLASS}
                style={INPUT_BOX_STYLE}
                {...invalidInputProps('financials_recency', errors.financials_recency, 'financials_recency-hint')}
              >
                <SelectValue placeholder="Select document recency" />
              </SelectTrigger>
              <SelectContent>
                {financialRecencyOptions.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError name="financials_recency" message={errors.financials_recency} />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
